import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import postFacebook from '@/assets/icons/post-facebook.svg';
import postLinkedIn from '@/assets/icons/post-linkedin.svg';
import postInstagram from '@/assets/icons/post-instagram.svg';
import postGoogle from '@/assets/icons/post-google.svg';
import { useToast } from '@/components/shadcn/use-toast';
import { Variant } from '@/components/compound-components/settings/integrations-tab/integrations-tab';
import { ChannelPage } from './connect-button';
import { UpdateSchedulingVariant, toChannelInput } from './disconnect-button';
import { Button } from '@/components/shadcn/button';
import { env } from 'next-runtime-env';
import {
  PostChannel,
  SchedulingVariantV1Channel,
  SchedulingVariantV1UpdateSuccess,
} from '@/__generated__/graphql';
import { Checkbox } from '@/components/shadcn/checkbox';
import { useOrganizationContext } from '@/contexts/organization-context';
import { Spinner } from '@/components/basic/spinner';
import { cleanTypeName } from '@/lib/utils';
import Image, { ImageProps } from 'next/image';
import { useSettingsConntext } from '@/contexts/settings-context';

export type PlatformConfig = {
  icon: ImageProps;
  thirdPartyId: 'facebook-business' | 'facebook' | 'custom-linkedin' | 'google';
  platform: 'meta' | 'linkedin' | 'google';
  channels: PostChannel[];
};

const socialMediaIcons: {
  Facebook: ImageProps;
  Instagram: ImageProps;
  LinkedIn: ImageProps;
  Google: ImageProps;
} = {
  Facebook: postFacebook,
  Instagram: postInstagram,
  LinkedIn: postLinkedIn,
  Google: postGoogle,
};

export default function PlatformPageLinking({
  variant,
  setLinkedSocialMedia,
  integrationConfig,
  currentTab,
}: {
  variant: Variant;
  setLinkedSocialMedia: (channels: SchedulingVariantV1Channel[]) => void;
  integrationConfig: {
    Meta: PlatformConfig;
    LinkedIn: PlatformConfig;
    Google: PlatformConfig;
  };
  currentTab: 'Meta' | 'LinkedIn' | 'Google';
}) {
  const { t } = useTranslation();
  const [updateSchedulingVariant] = useMutation(UpdateSchedulingVariant);
  const {
    setIntegrationShowPageLinking,
    integrationPageList,
    setIntegrationPageList,
    integrationPagesCheckedToLink,
    setIntegrationPagesCheckedToLink,
    integrationPlatformChannels,
  } = useSettingsConntext();
  const { organizationId } = useOrganizationContext();
  const [isLinkingPages, setIsLinkingPages] = useState(false);
  const { toast } = useToast();
  const [showLinking, setShowLinking] = useState(false);

  useEffect(() => {
    if (
      integrationPagesCheckedToLink &&
      integrationPagesCheckedToLink.length > 0
    ) {
      handleLinkPages();
    } else {
      setShowLinking(true);
    }
  }, []);

  const handleCheckedPagesListToggle = (page: ChannelPage) => () => {
    const currentIndex = integrationPagesCheckedToLink?.findIndex(
      (p: ChannelPage) => p.id === page.id
    );
    let newChecked = integrationPagesCheckedToLink && [
      ...integrationPagesCheckedToLink,
    ];

    if (currentIndex === -1) {
      // remove other pages of same type
      newChecked = newChecked?.filter((p) => p.type !== page.type);

      newChecked?.push(page);
    } else if (currentIndex) {
      newChecked?.splice(currentIndex, 1);
    }

    setIntegrationPagesCheckedToLink &&
      setIntegrationPagesCheckedToLink(newChecked);
  };

  const handleCancelClick = () => {
    setIntegrationPageList && setIntegrationPageList([]);
    setIntegrationPagesCheckedToLink && setIntegrationPagesCheckedToLink([]);
    setIntegrationShowPageLinking && setIntegrationShowPageLinking(false);
  };

  const handleLinkPages = async () => {
    try {
      setIsLinkingPages(true);
      const { variantId, existingChannels } = variant as Variant;

      let updateChannels = existingChannels ?? [];

      // add or update variant channels
      integrationPlatformChannels?.forEach(
        (socialMediaChannel: PostChannel) => {
          integrationPagesCheckedToLink?.forEach((page) => {
            const pageId = String(page.id);
            const channelIndex = updateChannels.findIndex(
              (c: SchedulingVariantV1Channel) =>
                (c.channel === socialMediaChannel &&
                  c.channel === page.type &&
                  c.pageId === pageId) ||
                (c.channel === socialMediaChannel &&
                  c.channel === page.type &&
                  Boolean(c.pageId) === false)
            );

            if (channelIndex > -1) {
              // update
              updateChannels[channelIndex].pageId = pageId;
              updateChannels[channelIndex].name = page.name;
            } else if (socialMediaChannel === page.type) {
              // add
              updateChannels = [
                ...updateChannels,
                { channel: page.type, pageId: pageId, name: page.name },
              ];
            }
          });
        }
      );

      let linkedPages = integrationPagesCheckedToLink;

      // new page linking flow currently only for meta.
      const platform = integrationConfig[currentTab].platform;

      if (platform === 'meta') {
        const INTEGRATION_PATH = `/${platform}/auth`;

        const response = await fetch(
          `${env('AUTHENTICATION_DOMAIN')}${INTEGRATION_PATH}/pages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              organizationId,
              pages: integrationPagesCheckedToLink,
              fbLoginForBusiness:
                integrationConfig[currentTab].thirdPartyId ===
                'facebook-business',
            }),
          }
        );

        const { data } = await response.json();
        if (data) {
          linkedPages = data;
        }
      }

      const { data: updateSchedulingVariantResultResponse } =
        await updateSchedulingVariant({
          variables: {
            input: {
              where: {
                id: variantId,
                organizationId,
              },
              set: {
                channels: updateChannels.map(toChannelInput),
              },
            },
          },
        });

      const { channels: resultChannels } = cleanTypeName(
        (
          updateSchedulingVariantResultResponse?.schedulingVariantV1
            ?.update as SchedulingVariantV1UpdateSuccess
        )?.result
      );

      setLinkedSocialMedia(resultChannels);

      const successMsg =
        'Access Granted For: \n' +
        linkedPages?.map(
          (page: ChannelPage) => `${page.type}: ${page.name} \n`
        );
      toast({
        description: successMsg,
        duration: 3000,
      });

      setIntegrationShowPageLinking && setIntegrationShowPageLinking(false);
    } catch (err) {
      toast({
        description: 'Something went wrong please try again.',
        duration: 3000,
      });
    } finally {
      setIsLinkingPages(false);
    }
  };
  return !showLinking ? (
    <></>
  ) : (
    <div className='absolute left-0 top-0 flex h-full w-full flex-col justify-between bg-white p-0.5 pt-4'>
      <div>
        {integrationConfig[currentTab].channels.map((channel) => (
          <div key={channel}>
            <div className='mt-6 h-px w-full bg-gray-300'></div>
            {channel !== 'LinkedIn' && (
              <div className='mx-auto -mt-7 mb-4 block w-auto max-w-28 bg-white px-2 pt-3 text-center text-lg uppercase'>
                {channel}
              </div>
            )}
            <ul className='w-full min-w-[360px] px-6'>
              {integrationPageList
                ?.filter((page: ChannelPage) => page.type === channel)
                .map((page: ChannelPage) => {
                  const labelId = `checkbox-list-secondary-label-${page.id}`;
                  return (
                    <li
                      className='flex items-center justify-between'
                      key={page.id}
                    >
                      <Button
                        className='mr-2 flex w-full justify-start'
                        variant={'ghost'}
                        onClick={handleCheckedPagesListToggle(page)}
                      >
                        <Image
                          src={socialMediaIcons[page.type]?.src}
                          width={socialMediaIcons[page.type]?.width}
                          height={socialMediaIcons[page.type]?.height}
                          alt={socialMediaIcons[page.type].alt}
                        />

                        <p className='ml-2 text-left' id={labelId}>
                          {page.name}
                        </p>
                      </Button>
                      <Checkbox
                        variant={'maggie'}
                        onClick={handleCheckedPagesListToggle(page)}
                        checked={
                          integrationPagesCheckedToLink?.findIndex(
                            (p) => p.id === page.id
                          ) !== -1
                        }
                      />
                    </li>
                  );
                })}
            </ul>
          </div>
        ))}
      </div>
      <div className='flex justify-between p-6 pl-8'>
        <Button variant={'maggie-secondary'} onClick={handleCancelClick}>
          {t('social-media-access-management.cancel')}
        </Button>
        <Button
          variant={'maggie-primary'}
          disabled={
            integrationPagesCheckedToLink?.length === 0 || isLinkingPages
          }
          onClick={handleLinkPages}
        >
          {isLinkingPages && <Spinner className='mr-1 w-4' />}
          {t('social-media-access-management.link-pages')}
        </Button>
      </div>
    </div>
  );
}
